import { Injectable, inject } from '@angular/core';
import { NotifyService } from '../../shared/notify/notify.service';
import { sanitizeUserFacingError } from '../utils/error-message.util';

/**
 * The console's alert entry point — ~1,300 call sites across the app.
 *
 * This is a thin adapter, not a second notification system: it forwards to
 * NotifyService, which owns the queue, the timers and the one on-screen stack
 * (see src/app/shared/notify). Anything wanting a detail line, an Undo action
 * or a custom duration should inject NotifyService directly; this service
 * stays exactly as narrow as its existing callers need.
 */
@Injectable({ providedIn: 'root' })
export class AlertService {

    private readonly notify = inject(NotifyService);

    // convenience methods
    //
    // `title` has never been rendered — call sites pass placeholders like
    // 'Success', 'true' and even the raw error object — so it stays accepted
    // and ignored. Surfacing it now would put "true" on screen 15 times over.
    success(message: string, title?: string) {
        this.notify.success(message);
    }

    /**
     * The last line before a failure reaches the screen, so it is where a
     * transport string is scrubbed.
     *
     * `extractErrorMessage` handles everything that flows through the global
     * ErrorInterceptor, but ~124 call sites — nearly all on `/admin` routes,
     * where the interceptor deliberately bails out so each page can own its
     * error UX — pass `error.message` to this method directly and bypass that
     * util entirely. Scrubbing here covers the interceptor AND every one of
     * them without editing 124 files, and keeps the rule in one place.
     *
     * Only the framework's own string and anything carrying an absolute URL are
     * replaced. A real backend message ("Signature name is already in use")
     * passes through untouched — hiding infrastructure detail must not swallow
     * copy that tells the reader what to fix.
     */
    error(message: string, title?: string) {
        this.notify.error(sanitizeUserFacingError(message));
    }

    info(message: string, title?: string) {
        this.notify.info(message);
    }

    /** Same scrub as `error()`: call sites pass `error.message` here too. */
    warning(message: string, title?: string) {
        this.notify.warning(sanitizeUserFacingError(message));
    }

}
